import { languageDisplayLabel, LanguagesEnum } from '@/api/enums';
import { CourseContent } from '@/client/courses';
import {
  ContentFrameVideo,
  PreviewAspectRatioFrame,
} from '@/components/courses/content-frame.styles';
import { ContinueOrFinishButton } from '@/components/courses/ContinueOrFinishButton';
import { CourseModuleFrame } from '@/components/courses/CourseModuleFrame';
import { VideoFallbackMessage } from '@/components/courses/VideoFallbackMessage';
import { FlexContainer } from '@/ui/styled-ui';
import { getCurrentVideoFile } from '@/utils/helpers';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledOverlayFallback = styled(VideoFallbackMessage)`
  &.p-inline-message {
    position: absolute;
    z-index: 2;
    margin: var(--small-padding);
    width: fit-content;
    background: color-mix(
      in srgb,
      var(--blue-pale) 90%,
      transparent
    ) !important;
  }
`;

const StyledPlayOverlay = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const StyledPlayButton = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--blue-dark);
  color: var(--white-main);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  box-shadow: 0 0 0 0 var(--blue-opacity);
  animation: pulse-ring 2s ease-out infinite;

  @keyframes pulse-ring {
    0% {
      box-shadow: 0 0 0 0 var(--blue-opacity);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(63, 157, 223, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(63, 157, 223, 0);
    }
  }

  &:hover {
    transform: scale(1.05);
  }
`;

type CourseVideoModuleProps = {
  moduleContent: CourseContent;
  language: LanguagesEnum;
  currentModuleIndex: number;
  modules: CourseContent[];
  isContentStarted: boolean;
  rtl: boolean;
  isCompact: boolean;
  containerMaxWidth: string | undefined;
  onLanguageChange: 'restart' | 'resume';
  onEnded?: () => void;
  onPlaybackError?: (video: HTMLVideoElement) => void;
  onContinue: () => void;
  onFinish: () => void;
};

export const CourseVideoModule: React.FC<CourseVideoModuleProps> = ({
  moduleContent,
  language,
  currentModuleIndex,
  modules,
  isContentStarted,
  rtl,
  isCompact,
  containerMaxWidth,
  onLanguageChange,
  onEnded,
  onPlaybackError,
  onContinue,
  onFinish,
}) => {
  const { t } = useTranslation();
  const [hasEnded, setHasEnded] = useState<boolean>(false);
  const [showControls, setShowControls] = useState(!isCompact);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasStartedRef = useRef(false);
  const resumeTimeRef = useRef(0);
  const pendingResumeRef = useRef<number | null>(null);
  const currentUrlRef = useRef<string | undefined>(undefined);

  // When the language changes mid-playback and actually swaps the video file,
  // resume the new-language video from the same position instead of resetting
  // to the poster frame. Skipped when both languages resolve to the same file
  // (e.g. a shared fallback video), since nothing reloads.
  useEffect(() => {
    const newUrl = getCurrentVideoFile(moduleContent, language)?.file
      ?.signedUrl;
    const urlChanged =
      currentUrlRef.current !== undefined && newUrl !== currentUrlRef.current;

    if (urlChanged && hasStartedRef.current && onLanguageChange === 'resume') {
      pendingResumeRef.current = resumeTimeRef.current;
    }

    currentUrlRef.current = newUrl;
  }, [language, moduleContent, onLanguageChange]);

  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (pendingResumeRef.current !== null) {
      video.currentTime = Math.min(pendingResumeRef.current, video.duration);
      pendingResumeRef.current = null;
      void video.play();
      return;
    }

    if (!video.autoplay && !hasStartedRef.current) {
      video.currentTime = Math.min(5, video.duration);
    }
  }, []);

  const handlePlay = useCallback(() => {
    const video = videoRef.current;
    if (video && !hasStartedRef.current) {
      hasStartedRef.current = true;
      video.currentTime = 0;
      setShowControls(true);
    }
  }, []);

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (video && hasStartedRef.current && pendingResumeRef.current === null) {
      resumeTimeRef.current = video.currentTime;
    }
  }, []);

  const handleEnded = () => {
    setHasEnded(true);
    onEnded?.();
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
  };

  const videoResult = getCurrentVideoFile(moduleContent, language);
  if (!videoResult) return null;

  return (
    <CourseModuleFrame>
      <FlexContainer
        direction="column"
        style={
          isCompact
            ? { width: '100%' }
            : { width: '70%', maxWidth: containerMaxWidth, margin: 'auto' }
        }
      >
        {videoResult.isFallback && !isCompact && (
          <VideoFallbackMessage
            severity="info"
            text={t('course.video.fallback.playback.v2', {
              fallbackLanguage:
                languageDisplayLabel[
                  videoResult.fallbackLanguage ?? LanguagesEnum.EN
                ],
            })}
            className="w-full mb-2"
          />
        )}
        <PreviewAspectRatioFrame style={{ width: '100%', margin: 0 }}>
          {videoResult.isFallback && isCompact && (
            <StyledOverlayFallback
              severity="info"
              text={t('course.video.fallback.playback.short', {
                selectedLanguage: languageDisplayLabel[language],
                fallbackLanguage:
                  languageDisplayLabel[
                    videoResult.fallbackLanguage ?? LanguagesEnum.EN
                  ],
              })}
            />
          )}
          <ContentFrameVideo
            ref={videoRef}
            playsInline
            webkit-playsinline
            src={videoResult.file.signedUrl}
            controls={showControls}
            controlsList="nodownload"
            onContextMenu={(e) => e.preventDefault()}
            autoPlay={!isCompact}
            onError={(event) => onPlaybackError?.(event.currentTarget)}
            onLoadedMetadata={handleLoadedMetadata}
            onPlay={handlePlay}
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleEnded}
          />
          {!showControls && (
            <StyledPlayOverlay onClick={() => videoRef.current?.play()}>
              <StyledPlayButton>
                <i className="pi pi-play" />
              </StyledPlayButton>
            </StyledPlayOverlay>
          )}
        </PreviewAspectRatioFrame>
      </FlexContainer>

      {isCompact ? (
        hasEnded && (
          <div
            style={{
              position: 'absolute',
              // sits just above the native video control bar
              bottom: '65px',
              right: 'var(--small-padding)',
              zIndex: 2,
            }}
          >
            <ContinueOrFinishButton
              currentModule={currentModuleIndex}
              modules={modules}
              isCurrentModuleCompleted={hasEnded}
              isContentStarted={isContentStarted}
              attemptLanguage={language}
              onContinue={onContinue}
              onFinish={onFinish}
              rtl={rtl}
            />
          </div>
        )
      ) : (
        <ContinueOrFinishButton
          currentModule={currentModuleIndex}
          modules={modules}
          isCurrentModuleCompleted={hasEnded}
          isContentStarted={isContentStarted}
          attemptLanguage={language}
          shouldWrapInStickyContainer
          onContinue={onContinue}
          onFinish={onFinish}
          rtl={rtl}
        />
      )}
    </CourseModuleFrame>
  );
};
